import { Text } from '@billy/design-system'
import { Search, SkeletonLoader } from '@shinetools/lumen-react'
import { ChangeEvent, ReactElement, useMemo, useState } from 'react'

import { useBalanceLabels } from '@features/balanceLedger'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { AccountLabel } from '../../../../elements/AccountLabel'
import { useFetchCreditorBalanceSummaryAccounts } from '../../../../hooks/useFetchCreditorBalanceSummaryAccounts'
import { FilterOptionRow } from '../../FilterChipShell/styles'
import {
  AccountList,
  AccountSkeletonLabel,
  AccountSkeletonRow,
  BodyContainer,
  EmptyState,
  SearchWrapper,
} from './styles'

type AccountsFilterBodyProps = {
  selectedIds: string[]
  onChange: (ids: string[]) => void
  encodedPeriod?: string
  enabled?: boolean
}

export const AccountsFilterBody = ({
  selectedIds,
  onChange,
  encodedPeriod,
  enabled = false,
}: AccountsFilterBodyProps): ReactElement => {
  const { bt } = useBalanceLabels()
  const { organization } = useUserOrganization()
  const [search, setSearch] = useState('')

  const { accounts, isLoading } = useFetchCreditorBalanceSummaryAccounts({
    organizationId: organization?.id ?? '',
    period: encodedPeriod,
    enabled,
  })

  const filteredAccounts = useMemo(() => {
    const needle = search.trim().toLowerCase()

    if (!needle) {
      return accounts
    }

    return accounts.filter(
      (account) => account.accountNumber.toLowerCase().includes(needle) || account.name.toLowerCase().includes(needle),
    )
  }, [search, accounts])

  const toggleAccount = (contactId: string) => {
    if (selectedIds.includes(contactId)) {
      onChange(selectedIds.filter((id) => id !== contactId))
    } else {
      onChange([...selectedIds, contactId])
    }
  }

  const renderList = () => {
    if (isLoading) {
      return (
        <AccountList>
          {Array.from({ length: 3 }, (_, index) => (
            <AccountSkeletonRow data-testid="account-skeleton-row" key={index}>
              <SkeletonLoader height={20} size="small" width={20} />
              <AccountSkeletonLabel>
                <SkeletonLoader height={20} size="small" width="100%" />
              </AccountSkeletonLabel>
            </AccountSkeletonRow>
          ))}
        </AccountList>
      )
    }
    if (filteredAccounts.length === 0) {
      return (
        <EmptyState>
          <Text variant="bodyMD">{bt('filters.account.no_results')}</Text>
        </EmptyState>
      )
    }
    return (
      <AccountList>
        {filteredAccounts.map((account) => (
          <FilterOptionRow
            key={account.contactId}
            size="l"
            value={account.contactId}
            checked={selectedIds.includes(account.contactId)}
            onChange={() => toggleAccount(account.contactId)}
          >
            <AccountLabel accountNumber={account.accountNumber} name={account.name} />
          </FilterOptionRow>
        ))}
      </AccountList>
    )
  }

  return (
    <BodyContainer>
      <SearchWrapper>
        <Search
          rounded
          noDropdown
          onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
          placeholder={bt('filters.account.search_placeholder')}
          size="small"
          value={search}
        />
      </SearchWrapper>
      {renderList()}
    </BodyContainer>
  )
}
